import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Download, Fingerprint, RefreshCw, Search } from "lucide-react";
import {
  buildErrorEventsCsv,
  downloadCsv,
  errorEventsCsvFilename,
  type ExportableErrorEvent,
} from "@/lib/errorEventsCsv";
import { DRILLDOWN_ANCHOR_ID, onApplyDrilldownFilters } from "@/lib/runtimeHealthBus";
import { useToast } from "@/hooks/use-toast";

type ErrorRow = {
  id: string;
  created_at: string;
  error_type: string;
  message: string;
  route: string;
  source: string | null;
  lineno: number | null;
  colno: number | null;
  stack: string | null;
  release: string | null;
  app_version: string | null;
  build_commit: string | null;
  fingerprint: string | null;
  session_id: string | null;
};

const PAGE_SIZE = 25;

type Filters = {
  q: string;
  route: string;
  session: string;
  fingerprint: string;
  release: string;
  from: string;
  to: string;
};

const EMPTY: Filters = { q: "", route: "", session: "", fingerprint: "", release: "", from: "", to: "" };

export function FrontendErrorDrilldownPanel({
  onFiltersChange,
}: {
  onFiltersChange?: (filters: Filters) => void;
} = {}) {
  const { toast } = useToast();
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<ErrorRow[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    let query = supabase
      .from("frontend_error_events")
      .select(
        "id, created_at, error_type, message, route, source, lineno, colno, stack, release, app_version, build_commit, fingerprint, session_id",
        { count: "exact" },
      )
      .order("created_at", { ascending: false })
      .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

    if (applied.q) query = query.or(`message.ilike.%${applied.q}%,stack.ilike.%${applied.q}%`);
    if (applied.route) query = query.ilike("route", `%${applied.route}%`);
    if (applied.session) query = query.eq("session_id", applied.session);
    if (applied.fingerprint) query = query.eq("fingerprint", applied.fingerprint);
    if (applied.release) query = query.eq("release", applied.release);
    if (applied.from) query = query.gte("created_at", new Date(applied.from).toISOString());
    if (applied.to) query = query.lte("created_at", new Date(applied.to).toISOString());

    const { data, error: err, count: total } = await query;
    if (err) setError(err.message);
    else {
      setRows((data ?? []) as ErrorRow[]);
      setCount(total ?? 0);
    }
    setLoading(false);
  }, [applied, page]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => onApplyDrilldownFilters((incoming) => {
    const next = { ...EMPTY, ...incoming } as Filters;
    setFilters(next);
    setApplied(next);
    setPage(0);
  }), []);

  useEffect(() => {
    onFiltersChange?.(applied);
  }, [applied, onFiltersChange]);

  const exportCsv = async () => {
    setExporting(true);
    let query = supabase
      .from("frontend_error_events")
      .select(
        "id, created_at, error_type, message, route, source, lineno, colno, stack, release, app_version, build_commit, fingerprint, session_id",
      )
      .order("created_at", { ascending: false })
      .limit(5000);
    if (applied.q) query = query.or(`message.ilike.%${applied.q}%,stack.ilike.%${applied.q}%`);
    if (applied.route) query = query.ilike("route", `%${applied.route}%`);
    if (applied.session) query = query.eq("session_id", applied.session);
    if (applied.fingerprint) query = query.eq("fingerprint", applied.fingerprint);
    if (applied.release) query = query.eq("release", applied.release);
    if (applied.from) query = query.gte("created_at", new Date(applied.from).toISOString());
    if (applied.to) query = query.lte("created_at", new Date(applied.to).toISOString());

    const { data, error: err } = await query;
    setExporting(false);
    if (err) {
      toast({ title: "Export failed", description: err.message, variant: "destructive" });
      return;
    }
    const events = (data ?? []) as ExportableErrorEvent[];
    if (events.length === 0) {
      toast({ title: "Nothing to export", description: "No events match these filters." });
      return;
    }
    downloadCsv(errorEventsCsvFilename(applied), buildErrorEventsCsv(events));
    toast({
      title: `Exported ${events.length} event${events.length === 1 ? "" : "s"}`,
      description: "Emails, tokens and IDs are redacted in the file.",
    });
  };

  const groups = useMemo(() => {
    const map = new Map<string, { fingerprint: string; count: number; message: string; routes: Set<string> }>();
    for (const r of rows) {
      const fp = r.fingerprint ?? "unfingerprinted";
      const entry = map.get(fp) ?? { fingerprint: fp, count: 0, message: r.message, routes: new Set<string>() };
      entry.count += 1;
      entry.routes.add(r.route);
      map.set(fp, entry);
    }
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const apply = () => {
    setPage(0);
    setApplied(filters);
  };

  return (
    <Card id={DRILLDOWN_ANCHOR_ID}>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Search className="size-4 text-primary" /> Error drilldown
          </CardTitle>
          <CardDescription>
            Search and paginate individual error events by page, session, release, fingerprint, and
            time range. Values are redacted client-side before they are stored.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => void exportCsv()} disabled={exporting || loading}>
            <Download className="size-3.5" /> {exporting ? "Exporting…" : "Export CSV"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field id="fe-q" label="Message / stack contains" value={filters.q} onChange={(v) => setFilters((f) => ({ ...f, q: v }))} />
          <Field id="fe-route" label="Page / route" value={filters.route} onChange={(v) => setFilters((f) => ({ ...f, route: v }))} />
          <Field id="fe-session" label="Session id" value={filters.session} onChange={(v) => setFilters((f) => ({ ...f, session: v }))} />
          <Field id="fe-fp" label="Fingerprint" value={filters.fingerprint} onChange={(v) => setFilters((f) => ({ ...f, fingerprint: v }))} />
          <Field id="fe-release" label="Release" value={filters.release} onChange={(v) => setFilters((f) => ({ ...f, release: v }))} />
          <Field id="fe-from" label="From" type="datetime-local" value={filters.from} onChange={(v) => setFilters((f) => ({ ...f, from: v }))} />
          <Field id="fe-to" label="To" type="datetime-local" value={filters.to} onChange={(v) => setFilters((f) => ({ ...f, to: v }))} />
          <div className="flex items-end gap-2">
            <Button onClick={apply} disabled={loading}>Search</Button>
            <Button
              variant="outline"
              onClick={() => {
                setFilters(EMPTY);
                setApplied(EMPTY);
                setPage(0);
              }}
            >
              Reset
            </Button>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            <AlertTriangle className="size-4" /> {error}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
          <span>
            {count} matching event{count === 1 ? "" : "s"} · page {page + 1} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 0 || loading} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page + 1 >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>

        {groups.length > 0 && (
          <div className="space-y-2">
            <h4 className="flex items-center gap-2 text-sm font-medium">
              <Fingerprint className="size-4" /> Deduped fingerprints on this page
            </h4>
            <div className="flex flex-wrap gap-2">
              {groups.map((g) => (
                <button
                  key={g.fingerprint}
                  className="rounded-md border border-border px-2 py-1 text-left text-xs hover:bg-muted"
                  onClick={() => {
                    setFilters((f) => ({ ...f, fingerprint: g.fingerprint }));
                    setApplied((f) => ({ ...f, fingerprint: g.fingerprint }));
                    setPage(0);
                  }}
                >
                  <span className="font-mono">{g.fingerprint}</span> · {g.count}× ·{" "}
                  <span className="text-muted-foreground">{[...g.routes].join(", ")}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-2">
          {rows.length === 0 && !loading && (
            <p className="text-sm text-muted-foreground">No events match these filters.</p>
          )}
          {rows.map((r) => (
            <div key={r.id} className="rounded-md border border-border p-3">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="secondary" className="font-mono">{r.error_type}</Badge>
                <span className="font-mono">{r.route}</span>
                <span>{new Date(r.created_at).toLocaleString()}</span>
                {r.release && <span className="font-mono">rel:{r.release}</span>}
                {r.build_commit && <span className="font-mono">sha:{r.build_commit.slice(0, 7)}</span>}
                {r.fingerprint && <span className="font-mono">fp:{r.fingerprint}</span>}
                {r.session_id && <span className="font-mono">sess:{r.session_id.slice(0, 8)}</span>}
              </div>
              <p className="mt-2 break-words text-sm">{r.message}</p>
              {r.source && (
                <p className="mt-1 font-mono text-xs text-muted-foreground break-all">
                  {r.source}
                  {r.lineno != null ? `:${r.lineno}` : ""}
                  {r.colno != null ? `:${r.colno}` : ""}
                </p>
              )}
              {r.stack && (
                <>
                  <button
                    className="mt-2 text-xs text-primary underline"
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                  >
                    {expanded === r.id ? "Hide stack" : "Show stack"}
                  </button>
                  {expanded === r.id && (
                    <pre className="mt-2 max-h-64 overflow-auto rounded bg-muted p-2 font-mono text-[11px] whitespace-pre-wrap">
                      {r.stack}
                    </pre>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  type = "text",
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs">{label}</Label>
      <Input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export default FrontendErrorDrilldownPanel;
